import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import set from 'lodash/set';
import { DeepPartial, IEnvProfile } from './env.types';
import { ENV_LIST_MAP } from './env.utils';

const envs: IEnvProfile = {
  instance: 'production',
  app: {
    weekStartsOn: 1,
    showTools: true,
    isCompact: false,
  },
  auth: {
    authUri: '',
    apiKey: '',
  },
  api: {
    translationApiUri: '',
    memberApiUri: '',
    memberApiV2Uri: '',
  },
  xmpp: {
    hilbiJid: '',
    hilbiId: '',
    serverUri: '',
    conferenceUri: '',
  },
  support: {
    baseUri: '',
    token: '',
  },
};

/**
 * Env service to provide ENVs for app.
 * Env are overridden form .env file
 *
 * !When you adding new env, add type to declaration.d declaration,
 * and add override configuration in ENV_LIST_MAP array, in utils to exposer envs,
 * and defaults in env.default file.
 */
class EnvService {
  private envProfile: IEnvProfile;
  private listeners: (() => void)[] = [];

  constructor() {
    this.envProfile = envs;
    this.initService();
  }

  private set env(newEnv: DeepPartial<IEnvProfile>) {
    this.envProfile = merge(this.envProfile, newEnv);
    this.emitChange();
  }

  /**
   * Function to override config for given server version with local environment
   */
  private initService = () => {
    const finalProfile: IEnvProfile = cloneDeep(this.envProfile);

    /**
     * Iterate through overrides, and replace them in config,
     * if value exist in env
     */
    for (const override of ENV_LIST_MAP) {
      const [envValue, configKey, transform] = override;

      if (envValue) {
        // set value for dynamic nested key
        set(finalProfile, configKey, transform ? transform(envValue) : envValue);
      }
    }

    this.env = finalProfile;
  };

  public subscribe = (callback: () => void) => {
    this.listeners = [...this.listeners, callback];

    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback);
    };
  };

  public getSnapshot = () => {
    return this.envProfile as Readonly<IEnvProfile>;
  };

  public setEnv = (newEnv: DeepPartial<IEnvProfile>) => {
    this.env = newEnv;
  };

  private emitChange = () => {
    for (const listener of this.listeners) {
      listener();
    }
  };
}

const envService = new EnvService();

export default envService;
